import React, { useEffect, useState } from "react";
import AdminDashLayout from "../../../Components/Admin/AdminDashLayout";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Modal,
  Button,
  Table,
  Tag,
  Dropdown,
  Space,
  Form,
  Select,
  Input,
  Menu,
  DatePicker,
  Spin,
  Tooltip,
} from "antd";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { DeleteFilled, MoreOutlined } from "@ant-design/icons";
import useNavHeading from "../../../Hooks/CommonHooks/useNavHeading ";
import { useDispatch, useSelector } from "react-redux";
import {
  createbenefitThunk,
  fetchbenefitsThunk,
  updatebenefitThunk,
} from "../../../Store/Slices/Admin/benifits/benifitThunk";
import EditBenefitDrawer from "./EditBenefitDrawer";
import { fetchPayrollStaff } from "../../../Store/Slices/Finance/payroll/payroll.thunk";
import toast from "react-hot-toast";
import { Option } from "antd/es/mentions";
import TableShimmer from "../../../Components/Common/TableShimmer";
import { useTranslation } from "react-i18next";
import ProtectedSection from "../../../Routes/ProtectedRoutes/ProtectedSection";
import { PERMISSIONS } from "../../../config/permission";
import ProtectedAction from "../../../Routes/ProtectedRoutes/ProtectedAction";

const BenifitMain = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(10);
  const { t } = useTranslation("common");
  useNavHeading(t("Benefit & Compensation"));

  const {
    loading,
    benefits: benefitsData,
    totalPages,
    totalbenefits,
    currentPage,
  } = useSelector((state) => state.admin.benefits);

  const [staffs, setStaffs] = useState([]);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [viewDrawerVisible, setViewDrawerVisible] = useState(false);
  const [auditDrawerVisible, setAuditDrawerVisible] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const [benefitToIssue, setBenefitToIssue] = useState(null);
  const [page, setPage] = useState(1);

  const [issuedForUser, setIssuedForUser] = useState({
    type: null,
    issuedFor: [],
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    dispatch(fetchbenefitsThunk({ page, limit }));
    dispatch(fetchPayrollStaff({ type: "staff" })).then((action) => {
      setStaffs(action?.payload?.data || []);
    });
  }, [dispatch, page, limit]);
  const handleView = (record) => {
    setSelectedBenefit(record);
    setViewDrawerVisible(true);
  };

  // 🔹 Handle "View Audit"
  const handleAuditView = (record) => {
    setSelectedBenefit(record);
    setAuditDrawerVisible(true);
  };

  const handleEdit = (record) => {
    setSelectedBenefit(record);
    setEditDrawerVisible(true);
  };

  const showIssueModal = (record) => {
    setIssuedForUser({ issuedFor: [], startDate: null, endDate: null });

    setBenefitToIssue(record);
    setIssueModalVisible(true);
  };

  const handleIssue = () => {
    if (!issuedForUser.issuedFor.length)
      return toast.error(t("Please select staff"));
    if (!issuedForUser.startDate)
      return toast.error(t("Please select start date"));
    if (!issuedForUser.endDate) return toast.error(t("Please select end date"));

    const formattedIssuedFor = issuedForUser.issuedFor.map((id) => ({
      staffId: id,
      startDate: issuedForUser.startDate,
      endDate: issuedForUser.endDate,
    }));

    dispatch(
      updatebenefitThunk({
        benefitId: benefitToIssue._id,
        updatedData: { issuedForNew: formattedIssuedFor },
      }),
    );

    setIssueModalVisible(false);
  };

  const handleDeleteIssue = (issueToDelete) => {
    if (!selectedBenefit?._id) return;

    Modal.confirm({
      title: "Are you sure you want to delete this record?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        const updatedIssuedFor = selectedBenefit.issuedFor.filter(
          (item) => item._id !== issueToDelete._id,
        );

        dispatch(
          updatebenefitThunk({
            benefitId: selectedBenefit._id,
            updatedData: {
              issuedFor: updatedIssuedFor,
            },
          }),
        );

        setSelectedBenefit((prev) => ({
          ...prev,
          issuedFor: updatedIssuedFor,
        }));

        toast.success("Audit record removed successfully");
      },
    });
  };

  const benefitColumns = [
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Name")}
        </span>
      ),
      dataIndex: "benefitName",
      key: "benefitName",
      render: (value) => (
        <span className="text-sm font-semibold text-gray-800">{value}</span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Employee Contribution")}
        </span>
      ),
      dataIndex: "employeeContribution",
      key: "employeeContribution",
      render: (value) => (
        <span className="text-sm text-gray-600">{value}%</span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Tenure")}
        </span>
      ),
      dataIndex: "tenure",
      key: "tenure",
      render: (value) => <span className="text-sm text-gray-600">{value}</span>,
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Registration is open")}
        </span>
      ),
      dataIndex: "activeForApply",
      key: "activeForApply",
      render: (status) => (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
            status === true
              ? "bg-green-50 text-green-600 border-green-200"
              : "bg-red-50 text-red-500 border-red-100"
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
              status === true ? "bg-green-500" : "bg-red-400"
            }`}
          />
          {status === true ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Actions")}
        </span>
      ),
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => handleView(record)}>
              {t("View")}
            </Menu.Item>
            <ProtectedAction requiredPermission={PERMISSIONS.VIEW_AUDIT}>
              <Menu.Item onClick={() => handleAuditView(record)}>
                {t("View Audit")}
              </Menu.Item>
            </ProtectedAction>
            <ProtectedAction requiredPermission={PERMISSIONS.EDIT_BENEFITS}>
              <Menu.Item onClick={() => handleEdit(record)}>
                {t("Edit")}
              </Menu.Item>
            </ProtectedAction>
            <ProtectedAction requiredPermission={PERMISSIONS.ISSUE_BENEFITS}>
              <Menu.Item onClick={() => showIssueModal(record)}>
                {t("Issue")}
              </Menu.Item>
            </ProtectedAction>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <button className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
              <MoreOutlined className="text-base" />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <AdminDashLayout>
      <ProtectedSection
        requiredPermission={PERMISSIONS.VIEW_BENEFITS_MANAGEMENT}
        title={"Benefits Management"}
      >
        <div className="p-4 w-full bg-gray-50 min-h-screen ">
          {/* ---------- Header Section ---------- */}
          <div className="flex flex-row w-full justify-between bg-white rounded-xl shadow-sm p-4 mb-4">
            <div className="p-2 rounded-xl ">
              <h2 className="page-heading">{t("Manage Benefits")}</h2>
              {/* <p className="mt-1 ">
              Manage all employee benefits and compensations
            </p> */}
            </div>
            <div>
              <ProtectedAction requiredPermission={PERMISSIONS.ADD_BENEFIT}>
                <button
                  onClick={() => navigate("/add-benifit")}
                  className="btn-gradient py-2"
                >
                  + {t("Add Benefit")}
                </button>
              </ProtectedAction>
              <ProtectedAction
                requiredPermission={PERMISSIONS.MANAGE_APPROVALS}
              >
                <button
                  onClick={() => navigate("/manager-approvals")}
                  className="bg-pink-200 text-fuchsia-700 hover:bg-pink-100 m-2 p-2 rounded-md"
                >
                  {t("Manager Approvals")}
                </button>
              </ProtectedAction>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {loading ? (
              <TableShimmer columns={5} />
            ) : (
              <div className="bg-white  mb-8">
                <Table
                  columns={benefitColumns}
                  dataSource={benefitsData || []}
                  rowKey="_id"
                  loading={loading}
                  className=""
                  pagination={{
                    current: page,
                    total: totalbenefits,
                    pageSize: limit,
                    showSizeChanger: true,
                    pageSizeOptions: ["5", "10", "20", "50", "100", "500"],
                    onChange: (page, pageSize) => {
                      setPage(page);
                      setLimit(pageSize);
                    },
                  }}
                />
              </div>
            )}
          </div>

          <Drawer
            title={
              <div className="text-purple-600 font-semibold text-lg">
                {selectedBenefit
                  ? `${t("View")} ${selectedBenefit.benefitName}`
                  : t("View Benefit")}
              </div>
            }
            placement="right"
            width="70%"
            onClose={() => setViewDrawerVisible(false)}
            open={viewDrawerVisible}
            bodyStyle={{ padding: "24px" }}
          >
            {selectedBenefit && (
              <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
                <h3 className="text-xl font-bold mb-4 text-gray-700">
                  {t("Benefit Details")}
                </h3>
                <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        {t("Benefit Type")}
                      </dt>
                      <dd className="mt-1 text-base text-gray-800">
                        {selectedBenefit.benefitType}
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        {t("Employee Contribution")}
                      </dt>
                      <dd className="mt-1 text-base text-gray-800">
                        {selectedBenefit.employeeContribution}%
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        {t("Company Contribution")}
                      </dt>
                      <dd className="mt-1 text-base text-gray-800">
                        {selectedBenefit.companyContribution}%
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        {t("Registration is open")}
                      </dt>
                      <dd className="mt-1 text-base w-20 text-center">
                        <Tag
                          color={
                            selectedBenefit.activeForApply ? "green" : "volcano"
                          }
                        >
                          {selectedBenefit.activeForApply ? "Yes" : "No"}
                        </Tag>
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        {t("Tenure")}
                      </dt>
                      <dd className="mt-1 text-base text-gray-800">
                        {selectedBenefit.tenure}
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        {t("Employment Status")}
                      </dt>
                      <dd className="mt-1 text-base text-gray-800">
                        {selectedBenefit.employmentStatus}
                      </dd>
                    </div>

                    <div className="md:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">
                        {t("Departments (Eligibility)")}
                      </dt>
                      <dd className="mt-1 text-base text-gray-800">
                        {selectedBenefit?.department?.join(", ")}
                      </dd>
                    </div>
                  </dl>

                  {/* 🔹 Show Documents as images */}
                  {selectedBenefit?.documents?.length > 0 && (
                    <div className="mt-4">
                      <dt className="text-sm font-medium text-gray-500">
                        {t("Attached Documents")}
                      </dt>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
                        {selectedBenefit.documents.map((doc, i) => (
                          <a
                            key={i}
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block border rounded-lg overflow-hidden hover:shadow-lg"
                          >
                            <img
                              src={doc.url}
                              alt={doc.name}
                              className="w-full h-32 object-cover"
                            />
                            <p className="text-center text-sm p-1 bg-gray-100">
                              {doc.name}
                            </p>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Drawer>
          <Drawer
            title={
              <span className="text-purple-600 font-bold text-lg">
                {t("Audit Log")}
              </span>
            }
            placement="right"
            width="50%"
            onClose={() => setAuditDrawerVisible(false)}
            open={auditDrawerVisible}
          >
            {selectedBenefit?.issuedFor?.length > 0 ? (
              selectedBenefit.issuedFor.map((issue) => {
                const selectedUser = staffs?.find(
                  (i) => i._id === issue.staffId,
                );

                if (!selectedUser) {
                  return (
                    <p key={issue._id} className="text-purple-600 font-medium">
                      {t("No")} {t("data available")}
                    </p>
                  );
                }

                return (
                  <div
                    key={issue._id}
                    className="flex flex-row gap-2 items-center border-2 border-purple-400 mb-2 justify-between rounded-lg bg-purple-50"
                  >
                    {/* User Info */}
                    <div className="p-5">
                      <div className="flex items-baseline">
                        <h4 className="font-semibold text-purple-700 text-lg mb-3">
                          {t("User Details")}
                        </h4>
                        <Tooltip title="Delete this record">
                          <button
                            className="text-red-600 font-extrabold hover:scale-105 ms-2"
                            onClick={() => handleDeleteIssue(issue)}
                          >
                            <DeleteOutlined />
                          </button>
                        </Tooltip>
                      </div>

                      <p>
                        <strong>{t("Name")}:</strong> {selectedUser.firstName}{" "}
                        {selectedUser.lastName}
                      </p>

                      {selectedUser.employeeID && (
                        <p>
                          <strong>{t("Employee ID")}:</strong>{" "}
                          {selectedUser.employeeID}
                        </p>
                      )}

                      {selectedUser.studentID && (
                        <p>
                          <strong>{t("Student ID")}:</strong>{" "}
                          {selectedUser.studentID}
                        </p>
                      )}

                      {selectedUser.email && (
                        <p>
                          <strong>{t("Email")}:</strong> {selectedUser.email}
                        </p>
                      )}
                    </div>

                    {/* Dates */}
                    <div className="p-5">
                      <h4 className="font-semibold text-purple-700 text-lg mb-3">
                        {t("Dates")}
                      </h4>
                      <p>
                        <strong>{t("Start Date")}:</strong>{" "}
                        {issue.startDate
                          ? new Date(issue.startDate).toLocaleDateString(
                              "en-GB",
                            )
                          : t("N/A")}
                      </p>
                      <p>
                        <strong>{t("End Date")}:</strong>{" "}
                        {issue.endDate
                          ? new Date(issue.endDate).toLocaleDateString("en-GB")
                          : t("N/A")}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-purple-600 font-medium">
                {t("No issued records found.")}
              </p>
            )}
          </Drawer>

          <EditBenefitDrawer
            selectedBenefit={selectedBenefit}
            editDrawerVisible={editDrawerVisible}
            setEditDrawerVisible={setEditDrawerVisible}
          />

          <Modal
            title={t("Confirm Issue")}
            open={issueModalVisible}
            onOk={handleIssue}
            onCancel={() => setIssueModalVisible(false)}
            okText={t("Confirm")}
            cancelText={t("Cancel")}
            okButtonProps={{ className: "btn-gradient" }}
          >
            <Select
              mode="multiple"
              placeholder={t("Select Staff")}
              value={issuedForUser.issuedFor}
              onChange={(v) =>
                setIssuedForUser({ ...issuedForUser, issuedFor: v })
              }
              className="w-full"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {staffs?.map((st) => (
                <Option key={st._id} value={st._id}>
                  {st.firstName} {st.lastName} | {st.employeeID}
                </Option>
              ))}
            </Select>

            {/* Date Pickers */}
            <DatePicker
              placeholder={t("Start Date")}
              className="w-full mt-4"
              value={issuedForUser.startDate}
              onChange={(date) =>
                setIssuedForUser({ ...issuedForUser, startDate: date })
              }
            />

            <DatePicker
              placeholder={t("End Date")}
              className="w-full mt-4"
              value={issuedForUser.endDate}
              onChange={(date) =>
                setIssuedForUser({ ...issuedForUser, endDate: date })
              }
            />
          </Modal>
        </div>
      </ProtectedSection>
    </AdminDashLayout>
  );
};

export default BenifitMain;
